/**
 * Sanitizador de Nomes de Arquivo
 * 
 * Centraliza a sanitização de nomes de arquivo, removendo caracteres
 * inválidos para sistemas de arquivos (especialmente Windows).
 * 
 * @module filename.sanitizer
 */

/**
 * Sanitiza um nome de arquivo removendo caracteres inválidos
 * 
 * Remove ou substitui caracteres que são inválidos em nomes de arquivo:
 * - Caracteres reservados do Windows: < > : " / \ | ? *
 * - Espaços múltiplos
 * - Pontos no final do nome
 * 
 * @param fileName - Nome do arquivo a ser sanitizado
 * @param fallback - Nome a ser usado se o arquivo estiver vazio após sanitização (padrão: 'arquivo_sem_nome')
 * @returns Nome de arquivo sanitizado e seguro
 * 
 * @example
 * ```typescript
 * sanitizeFileName('arquivo<>teste.txt') // 'arquivo__teste.txt'
 * sanitizeFileName('arquivo  com  espaços.txt') // 'arquivo_com_espacos.txt'
 * sanitizeFileName('arquivo...') // 'arquivo'
 * sanitizeFileName('') // 'arquivo_sem_nome'
 * ```
 */
export function sanitizeFileName(fileName: string, fallback: string = 'arquivo_sem_nome'): string {
  if (!fileName || typeof fileName !== 'string') {
    return fallback;
  }

  try {
    // Remover caracteres inválidos para nomes de arquivos no Windows
    // Caracteres reservados: < > : " / \ | ? *
    let sanitized = fileName
      .replace(/[<>:"/\\|?*]/g, '_') // Substituir caracteres inválidos por underscore
      .replace(/\s+/g, '_') // Substituir espaços por underscore
      .replace(/__+/g, '_') // Substituir múltiplos underscores por um único
      .replace(/\.+$/, '') // Remover pontos no final
      .trim();

    // Se após sanitização ficou vazio, usar fallback
    if (!sanitized) {
      return fallback;
    }

    return sanitized;
  } catch (error) {
    console.error('Erro ao sanitizar nome de arquivo:', error);
    return fallback || 'arquivo_erro_sanitizacao';
  }
}

/**
 * Valida se um nome de arquivo é válido (sem sanitizar)
 * 
 * @param fileName - Nome do arquivo a ser validado
 * @returns true se o nome é válido, false caso contrário
 */
export function isValidFileName(fileName: string): boolean {
  if (!fileName || typeof fileName !== 'string') {
    return false;
  }

  // Verificar se contém caracteres inválidos
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(fileName)) {
    return false;
  }

  // Verificar se não está vazio após trim
  if (!fileName.trim()) {
    return false;
  }

  return true;
}

